import { Modal, Typography } from '@mui/material'
import { ChevronLeftMinor } from '@shopify/polaris-icons'
import { Formik } from 'formik'
import _ from 'lodash'
import React, { useMemo, useRef, useState } from 'react'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import { DocusignFieldTabLabel } from 'src/constants/docusign'
import { DocusignSigningField } from 'src/interfaces/docusign'
import tw from 'twin.macro'
import { Button } from '../shared/Button'
import { DocusignFieldsFormModalValues } from './DocusignFieldsFormModal.interface'
import { getValidationSchema } from './DocusignFieldsFormModal.validator'
import { DocusignFieldsFormModalInputList } from './DocusignFieldsFormModalInputList'

interface Props {
  isOpen?: boolean
  fields: Array<DocusignSigningField>
  onSubmit(
    values: Array<{
      tabLabel: string
      value: string
    }>,
  ): Promise<void>
  onClose(): void
}

export const DocusignFieldsFormModal = ({ isOpen, fields, onSubmit, onClose }: Props) => {
  const formikRef = useRef<any>()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const container = useMemo(() => document.getElementById('root'), [])

  const handleFormSubmit = async (values: DocusignFieldsFormModalValues) => {
    setIsLoading(true)
    setError(null)

    const mmsOregonAddressField = fields.find(
      (field) => field.tabLabel === DocusignFieldTabLabel.mmsOregonAddress,
    )

    try {
      const payload = { ...values }

      if (mmsOregonAddressField && values[DocusignFieldTabLabel.state] === 'OR') {
        payload[DocusignFieldTabLabel.mmsOregonAddress] = `${
          values[DocusignFieldTabLabel.address]
        }, ${values[DocusignFieldTabLabel.city]}, ${values[DocusignFieldTabLabel.state]}, ${
          values[DocusignFieldTabLabel.zipcode]
        }`
      }

      await onSubmit(
        Object.entries(payload).map((item) => ({
          tabLabel: item[0],
          value: !_.isNil(item[1]) ? item[1].toString() : '',
        })),
      )
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      // onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`w-full h-full flex flex-col justify-center backdrop-blur`}
    >
      <div css={tw`w-full max-h-full m-auto overflow-auto p-4`}>
        <div css={tw`max-w-[390px] m-auto flex-col bg-shades-0 rounded-2xl overflow-hidden`}>
          <Formik
            initialValues={{
              ...fields.reduce((res, item) => {
                if (item.tabType === 'checkbox') {
                  res[item.tabLabel] = item.value || false
                } else if (
                  [
                    DocusignFieldTabLabel.zipcode,
                    DocusignFieldTabLabel.prevZipcode1,
                    DocusignFieldTabLabel.prevZipcode2,
                    DocusignFieldTabLabel.w9Zipcode,
                  ].includes(item.tabLabel)
                ) {
                  res[item.tabLabel] = (item.value || '').slice(0, 5)
                } else {
                  res[item.tabLabel] = item.value || ''
                }

                return res
              }, {} as any),
            }}
            innerRef={formikRef}
            validationSchema={getValidationSchema(fields)}
            onSubmit={handleFormSubmit}
          >
            {({ handleSubmit }) => (
              <div css={tw`h-full w-full flex flex-col`}>
                <div css={tw`p-4 py-3`}>
                  <Button
                    color="grey"
                    size="medium"
                    onClick={onClose}
                    css={tw`w-[82px]`}
                    leftIcon={<ChevronLeftMinor width={20} />}
                    data-testid="contractors-onboarding-docusign-fields-back-btn"
                  >
                    Back
                  </Button>
                </div>
                <div css={tw`bg-shades-50 p-4 pt-8`}>
                  <Typography css={tw`mb-2`} variant="h2">
                    Personal details
                  </Typography>
                  <Typography css={tw`mb-4 text-shades-400`}>
                    To begin signing documents, please provide the following basic information.
                  </Typography>
                  <DocusignFieldsFormModalInputList fields={fields} />
                  <FormErrorMessage css={tw`mb-4`} error={error} />
                </div>
                <div css={tw`p-4`}>
                  <Button
                    className={tw`w-full`}
                    loading={isLoading}
                    onClick={() => handleSubmit()}
                    data-testid="contractors-onboarding-docusign-fields-submit-btn"
                  >
                    Submit and continue
                  </Button>
                </div>
              </div>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  )
}
